'use strict';

/*
 * EnvironmentLinks
 *
 * Resolves hrefs on anchors marked with `data-env-link` + `data-env-service`
 * into the correct per-environment URL for the service the page is currently
 * running in.
 *
 * Previously this logic was duplicated across DashboardAccount and PortalNav.
 * It now lives here, with a per-service alias table so cutovers to a new
 * hostname for a specific service+env no longer require code changes in every
 * caller — just a new entry under SERVICE_ENV_ALIASES.
 */
var EnvironmentLinks = {
  // serviceName -> env -> full host (no protocol, no path).
  // Matches are exact. Absence of an entry means "use the default pattern
  // (`${service}.${env}.${domain}`)" derived from the current window host.
  SERVICE_ENV_ALIASES: {
    'lending-web': {
      // The DNS subdomain is `lending`, not `lending-web`.
      dev: 'lending.dev.mogo.ca',
      qa: 'lending.qa.mogo.ca',
      staging: 'lending.staging.mogo.ca',
      prod: 'lending.mogo.ca'
    }
  },

  /**
   * Rewrite the href on every `[data-env-link]` anchor inside $node to point
   * at the current environment's instance of its `data-env-service`.
   *
   * Safe no-op when $node is missing or contains no matching anchors.
   */
  applyEnvironmentLinks: function applyEnvironmentLinks($node) {
    if (!$node) {
      return;
    }

    var $links = $node.find('[data-env-link]');

    if ($links.length === 0) {
      return;
    }

    var envDetails = EnvironmentLinks.getEnvironmentDetails(window.location.hostname);

    if (!envDetails) {
      return;
    }

    var protocol = window.location.protocol || 'https:';

    $links.each(function (_, anchor) {
      var serviceName = (anchor.getAttribute('data-env-service') || '').replace(/^\s+|\s+$/g, '');
      if (!serviceName) {
        return;
      }

      var path = (anchor.getAttribute('data-env-path') || '').replace(/^\s+|\s+$/g, '');
      var url = EnvironmentLinks.buildServiceUrl(serviceName, path, envDetails, protocol);

      if (url) {
        anchor.setAttribute('href', url);
      }
    });
  },
  getEnvironmentDetails: function getEnvironmentDetails(host) {
    var env = EnvironmentLinks.deriveEnvironmentFromHost(host);
    if (!env) {
      return null;
    }

    var domainOverride = EnvironmentLinks.getDomainOverride(host, env);
    var isMogoCa = host && host.slice(-'.mogo.ca'.length) === '.mogo.ca';
    var domain = domainOverride || (isMogoCa || env === 'prod' ? 'mogo.ca' : env === 'staging' ? 'mogofintech.ca' : 'mogocloud.ca');

    return {
      env: env,
      domain: domain
    };
  },
  getDomainOverride: function getDomainOverride(host, env) {
    if (!host) {
      return null;
    }

    if (env === 'staging' && host === 'local.mogo.ca') {
      return 'mogofintech.ca';
    }

    return null;
  },
  deriveEnvironmentFromHost: function deriveEnvironmentFromHost(host) {
    if (!host) {
      return null;
    }

    if (host === 'www.mogo.ca' || host === 'mogo.ca') {
      return 'prod';
    }

    var normalizedHost = host.toLowerCase();

    if (normalizedHost.indexOf('localhost') === 0) {
      return 'dev';
    }

    // Handle *.[env].mogo.ca pattern (e.g. www.qa.mogo.ca, lending.dev.mogo.ca)
    var mogoCaSuffix = '.mogo.ca';
    if (normalizedHost.slice(-mogoCaSuffix.length) === mogoCaSuffix) {
      var prefix = normalizedHost.slice(0, normalizedHost.length - mogoCaSuffix.length);
      if (prefix) {
        var prefixSegments = prefix.split('.');
        var lastSegment = prefixSegments[prefixSegments.length - 1];
        var knownEnvs = { 'qa': 'qa', 'dev': 'dev', 'staging': 'staging', 'prod': 'prod' };
        if (knownEnvs[lastSegment]) {
          return knownEnvs[lastSegment];
        }
      }
    }

    // Handle legacy domains (*.mogocloud.ca, *.mogofintech.ca)
    var segments = normalizedHost.split('.');
    var subdomain = segments[0];

    switch (subdomain) {
      case 'staging':
        return 'staging';
      case 'qa':
      case 'sandbox':
        return 'qa';
      case 'local':
        return 'staging';
      case 'dev':
        return 'dev';
      default:
        return null;
    }
  },
  buildServiceUrl: function buildServiceUrl(serviceName, path, envDetails, protocol) {
    if (!serviceName || !envDetails || !envDetails.env) {
      return null;
    }

    var normalizedProtocol = protocol.slice(-1) === ':' ? protocol : protocol + ':';
    var cleanPath = path ? path.charAt(0) === '/' ? path : '/' + path : '';

    var aliasHost = EnvironmentLinks._lookupAlias(serviceName, envDetails.env);
    if (aliasHost) {
      return normalizedProtocol + '//' + aliasHost + cleanPath;
    }

    if (!envDetails.domain) {
      return null;
    }

    var host = envDetails.env === 'prod' ? serviceName + '.' + envDetails.domain : serviceName + '.' + envDetails.env + '.' + envDetails.domain;

    return normalizedProtocol + '//' + host + cleanPath;
  },
  _lookupAlias: function _lookupAlias(serviceName, env) {
    var serviceEntry = EnvironmentLinks.SERVICE_ENV_ALIASES[serviceName];
    if (!serviceEntry) {
      return null;
    }
    return serviceEntry[env] || null;
  }
};